import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Printer, Sparkles, Truck, Wand2 } from "lucide-react";
import Nav from "@/components/Nav";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { inr, SIZES } from "@/lib/api";

export const CARICATURE_PLANS = [
  [Sparkles, "Digital poster", 399, ["High-resolution file", "Instant download after payment", "Add a dedication and gift it on WhatsApp"]],
  [Printer, "Printed A3 poster", 1000, ["11.7 × 16.5 in matte art paper, true 300 DPI", "Printed in India, delivered in about 2 weeks", "Digital file included"]],
];

export function CaricaturePlans({ compact = false }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {CARICATURE_PLANS.map(([I, t, p, pts]) => (
        <div key={t} className={`paper rounded-lg ${compact ? "p-6" : "p-7"}`} data-testid={`caricature-price-${p}`}>
          <I className="h-6 w-6 text-[#3B5BDB]" strokeWidth={1.5} />
          <h3 className="mt-4 text-lg font-semibold">{t}</h3>
          <p className="mt-1 font-serif text-3xl">{inr(p)}</p>
          <ul className="mt-4 space-y-1.5 text-sm text-[#5E5868]">{pts.map((x) => <li key={x}>· {x}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

const BOOK_FACTS = [
  ["Pages included", "20 pages (10 spreads). Books run from 12 to 120 pages."],
  ["Extra pages", `${inr(25)} per page beyond 20 — add as many photos as you like.`],
  ["Shipping", `${inr(99)} anywhere in India · free on orders above ${inr(1999)}.`],
  ["AI curation & light enhancement", "Included. Auto-pick, auto-layout and captions at no extra cost."],
  ["Hard cover & paper", "Wraparound printed hard cover, 170 gsm matte art paper, lay-flat friendly."],
  ["Taxes", "All prices include GST. No surprises at checkout."],
];

export default function Pricing() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") === "caricatures" ? "caricatures" : "books");
  useEffect(() => { document.title = "Sizes & pricing · Click, Print & Doodle"; }, []);
  const switchTab = (t) => { setTab(t); setSp(t === "books" ? {} : { tab: t }, { replace: true }); };
  const start = user ? "/new" : "/register";

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-12 sm:px-8 lg:pt-16" data-testid="pricing-page">
        <p className="overline">Sizes & pricing</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">Honest prices. <em className="text-[#3B5BDB]">Nothing hidden.</em></h1>
        <Tabs value={tab} onValueChange={switchTab} className="mt-10">
          <TabsList className="h-auto rounded-full bg-[#E8E2D9]/70 p-1">
            <TabsTrigger value="books" data-testid="pricing-tab-books" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white">Photo books</TabsTrigger>
            <TabsTrigger value="caricatures" data-testid="pricing-tab-caricatures" className="rounded-full px-5 py-2 text-sm data-[state=active]:bg-white">Caricature posters</TabsTrigger>
          </TabsList>

          <TabsContent value="books" data-testid="pricing-books-panel" className="mt-10">
            <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">Three shapes. One price per shape.</h2>
            <p className="mt-3 max-w-2xl text-sm text-[#5E5868] sm:text-base">Every book starts with 20 pages, AI curation and a printed hard cover. Pick the shape that suits your photos — square for everything, portrait for people, landscape for places.</p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {SIZES.map((s, i) => (
                <div key={s.key} data-testid={`pricing-size-${s.key}`} className="paper rounded-2xl p-7">
                  <div className={`flex h-36 items-end rounded-xl p-4 ${["tint-rose", "tint-honey", "tint-mint"][i]}`}>
                    <div className="page-shadow bg-[#FAF5EF]" style={{ height: s.ratio >= 1 ? 128 / s.ratio : 128, width: s.ratio >= 1 ? 128 : 128 * s.ratio }} />
                  </div>
                  <h3 className="mt-6 text-lg font-semibold">{s.label} <span className="font-normal text-[#8C8498]">· {s.dims}</span></h3>
                  <p className="mt-1 font-serif text-3xl">{inr(s.from)}</p>
                  <p className="mt-1 text-sm text-[#8C8498]">Includes 20 pages · +{inr(25)} per extra page · GST incl.</p>
                </div>
              ))}
            </div>
            <div className="mt-12 grid gap-10 lg:grid-cols-12">
              <dl className="grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:col-span-8" data-testid="pricing-book-facts">
                {BOOK_FACTS.map(([k, v]) => (
                  <div key={k} className="border-t border-[#E8E2D9] pt-4">
                    <dt className="text-sm font-semibold">{k}</dt>
                    <dd className="mt-1 text-sm leading-relaxed text-[#5E5868]">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="paper rounded-lg p-7 lg:col-span-4" data-testid="pricing-book-example">
                <p className="overline">Worked example</p>
                <p className="mt-3 font-serif text-2xl">Square 21×21 cm, 28 pages</p>
                <ul className="mt-4 space-y-2 text-sm text-[#5E5868]">
                  <li className="flex justify-between"><span>Book (20 pages)</span><span>{inr(1499)}</span></li>
                  <li className="flex justify-between"><span>8 extra pages × {inr(25)}</span><span>{inr(200)}</span></li>
                  <li className="flex justify-between"><span>Shipping</span><span>{inr(99)}</span></li>
                  <li className="flex justify-between border-t border-[#E8E2D9] pt-2 font-semibold text-[#26232E]"><span>Total</span><span>{inr(1798)}</span></li>
                </ul>
                <Link to={start} data-testid="pricing-start-book-btn" className="btn-primary mt-6 w-full">Start your book <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="caricatures" data-testid="pricing-caricatures-panel" className="mt-10">
            <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">One portrait. Two ways to keep it.</h2>
            <p className="mt-3 max-w-2xl text-sm text-[#5E5868] sm:text-base">Upload a photo and up to ten quirks. Previewing is free — you only pay when you love it. One portrait per upload.</p>
            <div className="mt-10 lg:max-w-3xl"><CaricaturePlans /></div>
            <dl className="mt-12 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:max-w-3xl" data-testid="pricing-caricature-facts">
              {[
                [Wand2, "What you get", "A classic big-head caricature with every quirk drawn in — and their name lettered at the bottom."],
                [Truck, "Printed poster delivery", "Shipping is included in the ₹1,000 price. About 2 weeks across India."],
              ].map(([I, k, v]) => (
                <div key={k} className="border-t border-[#E8E2D9] pt-4">
                  <dt className="flex items-center gap-2 text-sm font-semibold"><I className="h-4 w-4 text-[#3B5BDB]" />{k}</dt>
                  <dd className="mt-1 text-sm leading-relaxed text-[#5E5868]">{v}</dd>
                </div>
              ))}
            </dl>
            <Link to={user ? "/caricature" : "/register?next=/caricature"} data-testid="pricing-make-caricature-btn" className="btn-primary mt-10">Make a Caricature <ArrowRight className="h-4 w-4" /></Link>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
